import React, { useState, useEffect, useRef, useMemo } from "react";
import { Modal } from "@mantine/core";
import { notifyError } from "../../../../../utils/notify";
import { useResponsive } from "../../../../../context/ResponsiveContext";
import {
  GetTransactionsResponse,
  TransactionResponse,
  EditTransactionRequest,
  EditTransactionLineItemsRequest,
} from "../../../../../types/transactions";
import { useSalon } from "../../../../../context/SalonContext";
import { notifyServiceBookingError } from "../../../../../utils/serviceAvailabilityError";
import { BusinessInfo } from "../../../../../types/context";
import AddLineItemModal from "./AddLineItemModal";
import GiftCardPostCheckoutModal from "../QuickSaleModal/GiftCardPostCheckoutModal";
import TransactionBookedServiceModal from "./TransactionBookedServiceModal/TransactionBookedServiceModal";
import ProductForm from "./ProductForm";
import ApplyDiscountModal from "./ApplyDiscountModal";
import TransactionDetails from "./TransactionDetails";
import TransactionSummary from "./TransactionSummary";
import CheckoutPanel from "../../../../checkout/CheckoutPanel/CheckoutPanel";
import { useCheckoutState } from "../../../../checkout/CheckoutPanel/useCheckoutState";
import { LoyaltyRewardPick } from "../../../../rewards/LoyaltyRewardPickerModal";
import { useApp } from "../../../../../context/AppContext";
import { useTransaction } from "../../../../../context/TransactionContext";
import {
  BookedService,
  Product,
  TransactionServicePackage,
  PendingGiftCardLineItem,
} from "../../../../../types/transactions";
import { ServicePackage } from "../../../../../types/packages";
import { DiscountPreset } from "../../../../../types/discounts";
import BrandedLoadingOverlay from "../../../../common/BrandedLoadingOverlay/BrandedLoadingOverlay";
import usePermissions from "../../../../../hooks/usePermissions";
import FullscreenShell from "../../../../common/FullscreenShell/FullscreenShell";
import SurfacePaper from "../../../../common/surface/SurfacePaper";
import { computeTransactionTotals } from "../../../../../utils/transactionTotals";
import { useDebouncedLineItemQuantity } from "../../../../../hooks/useDebouncedLineItemQuantity";
import {
  buildDiscountableLines,
  buildLineDiscountsRequest,
  DiscountGroup,
  LineDiscountChange,
} from "../../../../../utils/lineItemDiscounts";

interface TransactionDetailModalProps {
  opened: boolean;
  onClose: () => void;
  transaction: GetTransactionsResponse | TransactionResponse | null;
  onTransactionUpdated: (
    updatedTransaction: GetTransactionsResponse | TransactionResponse,
  ) => void;
  onViewNewAppointment?: (transactionId: string) => void;
  /** Rebook in the host's flow; absent = the legacy RebookModal. */
  onRebook?: (
    transaction: GetTransactionsResponse | TransactionResponse,
  ) => void;
  isLoadingNewTransaction?: boolean;
  initialTab?: string | null;
}

export default function TransactionDetailModal({
  opened,
  onClose,
  transaction: transactionProp,
  onTransactionUpdated,
  onViewNewAppointment,
  onRebook,
  isLoadingNewTransaction = false,
  initialTab = null,
}: TransactionDetailModalProps) {
  // Optimistic appointment status from the header pill: shown immediately,
  // cleared (reverted) if the write fails, and dropped once the host's
  // transaction catches up or a different transaction / close happens.
  const [statusOverride, setStatusOverride] = useState<{
    id: string;
    status: string;
  } | null>(null);
  const transaction = useMemo(
    () =>
      transactionProp &&
      statusOverride &&
      statusOverride.id === transactionProp.transaction.id
        ? ({
            ...transactionProp,
            transaction: {
              ...transactionProp.transaction,
              appointment_status: statusOverride.status,
            },
          } as typeof transactionProp)
        : transactionProp,
    [transactionProp, statusOverride],
  );
  const propStatus = transactionProp?.transaction.appointment_status;
  const propId = transactionProp?.transaction.id;
  useEffect(() => {
    setStatusOverride((prev) =>
      prev && (prev.id !== propId || prev.status === propStatus) ? null : prev,
    );
  }, [propId, propStatus]);
  useEffect(() => {
    if (!opened) setStatusOverride(null);
  }, [opened]);

  const { isMobile } = useResponsive();
  const { businessInfo, refreshServices } = useSalon() as {
    businessInfo: BusinessInfo | null;
    refreshServices: () => Promise<unknown>;
  };
  const {
    addLineItemToTransaction,
    editTransaction,
    editTransactionLineItems,
    getTransaction,
    getServicePackages,
  } = useApp();
  const { deleteLineItem } = useTransaction();
  const perms = usePermissions();
  const calendarPerms = perms?.calendar;
  // With the new permission model, transaction editing is allowed if user has
  // calendar access. Calendar scope is always "own" or "all" (no "none"
  // option), so we check if perms exist.
  const canEditTransaction = calendarPerms != null;

  // Add line item modal state
  const [isAddLineItemModalOpen, setIsAddLineItemModalOpen] = useState(false);
  const [lineItemType, setLineItemType] = useState<
    "service" | "product" | "package" | "giftcard" | null
  >(null);

  // Gift Cards feature wall — surfaces the "Gift Card" add-item option (and its
  // add form). Requires the env flag AND the location having gift cards enabled
  // (businessInfo.gift_cards_enabled, from gift_card_configurations.is_enabled),
  // mirroring QuickSaleModal so a disabled location never sees the option. The
  // add form keeps its own is_enabled + amount-bounds check as a downstream guard.
  const giftCardsEnabled =
    process.env.REACT_APP_GIFT_CARDS_ENABLED === "true" &&
    businessInfo?.gift_cards_enabled === true;
  // After a paid checkout that sold a gift card, offer to attach the minted card
  // to a client / text the recipient a claim link (reuses the Quick Sale modal).
  const [showGiftCardPostCheckout, setShowGiftCardPostCheckout] =
    useState(false);

  // Edit modals state
  const [isEditServiceModalOpen, setIsEditServiceModalOpen] = useState(false);
  const [isEditProductModalOpen, setIsEditProductModalOpen] = useState(false);
  const [isDiscountModalOpen, setIsDiscountModalOpen] = useState(false);
  const [editingService, setEditingService] = useState<BookedService | null>(
    null,
  );
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  // The discount group being changed ("Change" on a summary row); null when
  // applying a new discount.
  const [discountGroupToChange, setDiscountGroupToChange] =
    useState<DiscountGroup | null>(null);
  // The quick discount whose "+ Add" opened the modal (preselected on every
  // line); null otherwise.
  const [quickDiscountPreset, setQuickDiscountPreset] =
    useState<DiscountPreset | null>(null);
  const discountableLines = useMemo(
    () => (transaction ? buildDiscountableLines(transaction) : []),
    [transaction],
  );

  // Checkout: an in-place screen swap inside this fullscreen modal. Back
  // preserves the operator's selections (method/card/package/override);
  // Cancel Checkout and modal close reset them.
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const { state: checkoutState, actions: checkoutActions } = useCheckoutState();

  // Loyalty reward picks are staged in Appointment Details (Rewards &
  // Promotions section) and survive the Details → Checkout screen swap so
  // CheckoutPanel can commit them as a single {points} redeem at Charge.
  // Cleared alongside the checkout state on modal close / rebook / cancel.
  const [stagedRewards, setStagedRewards] = useState<LoyaltyRewardPick[]>([]);

  // The salon's packages/specials, loaded once when the modal opens. Used to
  // gate the "Package or Special" add action (disabled with a Settings prompt
  // when none are configured) and to seed the picker without a second fetch.
  const [salonPackages, setSalonPackages] = useState<ServicePackage[]>([]);
  const hasPackagesConfigured = salonPackages.length > 0;

  // Status update state
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  // Error state
  const [errors, setErrors] = useState<{
    discount?: string;
    general?: string;
  }>({});

  // Function to refetch the transaction and update the UI
  const handleRefetchTransaction = async () => {
    if (!transaction) return;
    try {
      const refreshedTransaction = await getTransaction(
        transaction.transaction.id,
      );
      if (refreshedTransaction) {
        onTransactionUpdated(refreshedTransaction);
      }
    } catch (error) {
      console.error("Failed to refetch transaction:", error);
    }
  };

  // Keep the latest refetch closure in a ref so the "on open" effect can depend
  // only on the transaction id (honest deps) without re-running each render — and
  // without an eslint-disable for react-hooks/exhaustive-deps, a rule this CRA
  // base ESLint config doesn't load (referencing it fails the CI build).
  const refetchRef = useRef(handleRefetchTransaction);
  refetchRef.current = handleRefetchTransaction;

  // Refetch the full transaction (incl. refunds[]) when the modal opens for a
  // transaction, so refunds render on a plain refresh / re-open — not only right
  // after issuing one. This modal opens from the calendar's cached list, whose
  // payload omits the refunds array. Keyed by id: onTransactionUpdated preserves
  // the id, so this does not loop.
  const transactionId = transaction?.transaction.id ?? null;
  useEffect(() => {
    if (transactionId) {
      refetchRef.current();
    }
  }, [transactionId]);

  // Leave checkout and clear its selections whenever the modal closes or the
  // transaction changes (e.g. the rebook flow swaps `transaction` in place).
  // Staged rewards clear too — they're per-transaction UI state and would
  // otherwise carry across a rebook.
  useEffect(() => {
    setCheckoutOpen(false);
    checkoutActions.reset();
    setStagedRewards([]);
  }, [opened, transactionId, checkoutActions]);

  // Load the salon's packages/specials once when the modal opens.
  const salonId = businessInfo?.id ?? "";
  useEffect(() => {
    if (!opened || !salonId) return;
    let active = true;
    (async () => {
      try {
        const list = await getServicePackages(salonId);
        if (active) setSalonPackages(list ?? []);
      } catch (error) {
        console.error("Failed to load salon packages:", error);
        if (active) setSalonPackages([]);
      }
    })();
    return () => {
      active = false;
    };
  }, [opened, salonId, getServicePackages]);

  // Inline line-item quantity editing, shared by both panes: the detail rows
  // drive the steppers; the summary shows skeletons for the recalculating
  // amounts and disables Checkout while a save is in flight.
  const { displayQuantity, isSaving, isAnySaving, setQuantity } =
    useDebouncedLineItemQuantity(transaction, onTransactionUpdated);

  if (!transaction) {
    return null;
  }

  const handleAddLineItem = (
    type: "service" | "product" | "package" | "giftcard",
  ) => {
    setLineItemType(type);
    setIsAddLineItemModalOpen(true);
  };

  const handleLineItemAdded = async (lineItemData: any) => {
    try {
      const response = await addLineItemToTransaction(
        transaction.transaction.id,
        lineItemData,
      );
      if (response && response.line_items) {
        // Create updated transaction object with response data
        const updatedTransaction = {
          ...transaction,
          line_items: response.line_items,
        };

        // Update local state immediately for responsive UI
        if (onTransactionUpdated) {
          onTransactionUpdated(updatedTransaction);
        }
      }

      setIsAddLineItemModalOpen(false);
      setLineItemType(null);
    } catch (error) {
      console.error("Failed to add line item:", error);
      setIsAddLineItemModalOpen(false);
      setLineItemType(null);
      notifyServiceBookingError(error, {
        refreshServices,
        fallbackMessage: "Could not add the service. Please try again.",
      });
    }
  };

  const handleAddLineItemModalClose = () => {
    setIsAddLineItemModalOpen(false);
    setLineItemType(null);
  };

  const handleEditService = (service: any) => {
    setEditingService(service);
    setIsEditServiceModalOpen(true);
  };

  const handleEditProduct = (product: any) => {
    setEditingProduct(product);
    setIsEditProductModalOpen(true);
  };

  // Close the service / product edit modals and clear their editing target.
  // Shared by the success and error paths. (Packages are discounted via the
  // shared ApplyDiscountModal, so they have no dedicated edit modal.)
  const closeEditModals = () => {
    setIsEditServiceModalOpen(false);
    setIsEditProductModalOpen(false);
    setEditingService(null);
    setEditingProduct(null);
  };

  const handleLineItemUpdated = async (
    lineItemData: EditTransactionLineItemsRequest,
  ) => {
    try {
      const response = await editTransactionLineItems(lineItemData);
      if (response && response.line_items) {
        // Create updated transaction object with response data
        const updatedTransaction = {
          ...transaction,
          line_items: response.line_items,
        };

        // Update local state immediately for responsive UI
        if (onTransactionUpdated) {
          onTransactionUpdated(updatedTransaction);
        }
      }

      closeEditModals();
    } catch (error) {
      console.error("Failed to update line item:", error);
      closeEditModals();
    }
  };

  // Open the apply-discount modal: "Change" passes the discount group to
  // edit; no args = apply a new discount.
  const onOpenDiscountModal = (group?: DiscountGroup) => {
    // Clear any existing errors when opening modal
    setErrors({});
    setDiscountGroupToChange(group ?? null);
    setQuickDiscountPreset(null);
    setIsDiscountModalOpen(true);
  };

  // A quick discount's "+ Add": the apply modal with that preset preselected
  // on every line.
  const onQuickDiscount = (preset: DiscountPreset) => {
    setErrors({});
    setDiscountGroupToChange(null);
    setQuickDiscountPreset(preset);
    setIsDiscountModalOpen(true);
  };

  // Updated functions for processing submitted data
  const handleDiscountSubmitted = async (
    discountData: EditTransactionLineItemsRequest,
  ) => {
    try {
      // Discounts are now only applied to line items
      const response = await editTransactionLineItems(discountData);

      if (response && response.line_items) {
        // Create updated transaction object with response data
        const updatedTransaction = {
          ...transaction,
          line_items: response.line_items,
        };

        // Update local state immediately for responsive UI
        onTransactionUpdated(
          updatedTransaction as GetTransactionsResponse | TransactionResponse,
        );
      }

      setIsDiscountModalOpen(false);
      setDiscountGroupToChange(null);
      setQuickDiscountPreset(null);
    } catch (error) {
      console.error("Failed to update discount:", error);
      // Set error state for user feedback
      setErrors((prev) => ({
        ...prev,
        discount: "Failed to update discount. Please try again.",
      }));
      // Don't close modal on error - let user fix and retry
      throw error; // Re-throw to let modal handle the error
    }
  };

  // ApplyDiscountModal → PUT each changed line's full discount set.
  const handleApplyLineDiscounts = (changes: LineDiscountChange[]) =>
    handleDiscountSubmitted(buildLineDiscountsRequest(transaction, changes));

  const closeDiscountModal = () => {
    setIsDiscountModalOpen(false);
    setDiscountGroupToChange(null);
    setQuickDiscountPreset(null);
    setErrors({});
  };

  const handleStatusUpdate = async (newStatus: string) => {
    // Surface failures instead of swallowing them — the badge is a dropdown that
    // offers Cancelled/Declined, and a rejected write otherwise looks identical
    // to a no-op (spinner, badge snaps back, nothing said). Mirrors the calendar
    // pop-over's handlePreviewStatusUpdate.
    const notifyFailure = (error: unknown) => {
      console.error("Failed to update status:", error);
      notifyError(
        "We couldn't update the appointment status. Please try again.",
        { title: "Update failed" },
      );
    };

    const revert = () => setStatusOverride(null);
    setStatusOverride({ id: transaction.transaction.id, status: newStatus });

    try {
      setIsUpdatingStatus(true);
      const statusUpdateData: EditTransactionRequest = {
        transaction_id: transaction.transaction.id,
        appointment_status:
          newStatus as EditTransactionRequest["appointment_status"],
      };

      const response = await editTransaction(statusUpdateData);

      // A falsy response means the write didn't take — surface it rather than
      // silently leaving the badge on the old status.
      if (!response) {
        revert();
        notifyFailure(new Error("editTransaction returned no response"));
        return;
      }

      const updatedTransaction = {
        ...transaction,
        transaction: {
          ...transaction.transaction,
          appointment_status: newStatus,
        },
      };

      if (onTransactionUpdated) {
        onTransactionUpdated(updatedTransaction);
      }
    } catch (error) {
      revert();
      notifyFailure(error);
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleDeleteLineItem = async (
    lineItem:
      | BookedService
      | Product
      | TransactionServicePackage
      | PendingGiftCardLineItem,
  ) => {
    try {
      await deleteLineItem(lineItem.line_item_id);

      // Create updated transaction object with the item removed. The generic
      // DELETE /line-items/{uuid} removes any line type, so also drop it from
      // the service_packages and gift_cards arrays for an immediate UI update.
      const updatedTransaction = {
        ...transaction,
        line_items: {
          ...transaction.line_items,
          booked_services: transaction.line_items.booked_services.filter(
            (s) => s.line_item_id !== lineItem.line_item_id,
          ),
          products: transaction.line_items.products.filter(
            (p) => p.line_item_id !== lineItem.line_item_id,
          ),
          service_packages: (
            transaction.line_items.service_packages ?? []
          ).filter((p) => p.line_item_id !== lineItem.line_item_id),
          gift_cards: (transaction.line_items.gift_cards ?? []).filter(
            (g) => g.line_item_id !== lineItem.line_item_id,
          ),
        },
      };

      // Update local state immediately for responsive UI
      if (onTransactionUpdated) {
        onTransactionUpdated(updatedTransaction);
      }
    } catch (error) {
      console.error("Failed to delete line item:", error);
      throw error;
    }
  };

  const totals = computeTransactionTotals(transaction);

  const exitCheckout = () => {
    checkoutActions.reset();
    setCheckoutOpen(false);
  };

  return (
    <>
      <Modal
        opened={opened}
        onClose={onClose}
        fullScreen
        withCloseButton={false}
        closeOnEscape={false}
        closeOnClickOutside={false}
        styles={{
          body: { padding: 0, backgroundColor: "var(--surface-page)" },
        }}
      >
        {/* Loading overlay for when fetching new transaction after rebooking */}
        <BrandedLoadingOverlay visible={isLoadingNewTransaction} />

        {checkoutOpen ? (
          <CheckoutPanel
            transaction={transaction}
            remainingBalance={Number(totals.remainingBalance.toFixed(2))}
            businessInfo={businessInfo as BusinessInfo}
            state={checkoutState}
            actions={checkoutActions}
            onTransactionUpdated={onTransactionUpdated}
            onRefetchTransaction={handleRefetchTransaction}
            onBack={() => setCheckoutOpen(false)}
            onCancelCheckout={exitCheckout}
            onCloseModal={onClose}
            onPaymentComplete={async () => {
              // Capture before refetch: a paid gift-card line means the card just
              // minted, so advance to the attach/send fork after the refresh.
              const soldGiftCard =
                giftCardsEnabled &&
                (transaction.line_items.gift_cards ?? []).length > 0;
              exitCheckout();
              await handleRefetchTransaction();
              if (soldGiftCard) {
                setShowGiftCardPostCheckout(true);
              }
            }}
            stagedRewards={stagedRewards}
            onStagedRewardsChange={setStagedRewards}
          />
        ) : (
          <FullscreenShell
            onClose={onClose}
            left={
              <SurfacePaper>
                <TransactionDetails
                  transaction={transaction}
                  businessInfo={businessInfo}
                  onAddLineItem={handleAddLineItem}
                  onEditService={handleEditService}
                  onEditProduct={handleEditProduct}
                  onDeleteLineItem={handleDeleteLineItem}
                  onTransactionUpdated={onTransactionUpdated}
                  onStatusUpdate={handleStatusUpdate}
                  isUpdatingStatus={isUpdatingStatus}
                  errors={errors}
                  canEdit={canEditTransaction}
                  hasPackagesConfigured={hasPackagesConfigured}
                  giftCardsEnabled={giftCardsEnabled}
                  initialTab={initialTab}
                  displayQuantity={displayQuantity}
                  isSaving={isSaving}
                  setQuantity={setQuantity}
                />
              </SurfacePaper>
            }
            right={
              <TransactionSummary
                transaction={transaction}
                onStatusUpdate={handleStatusUpdate}
                isUpdatingStatus={isUpdatingStatus}
                onTransactionUpdated={onTransactionUpdated}
                onViewNewAppointment={onViewNewAppointment}
                onRebook={onRebook}
                canEdit={canEditTransaction}
                onProceedToCheckout={() => setCheckoutOpen(true)}
                onRefetchTransaction={handleRefetchTransaction}
                onOpenDiscountModal={onOpenDiscountModal}
                onQuickDiscount={onQuickDiscount}
                onDeleteDiscount={handleDiscountSubmitted}
                isLineItemSaving={isSaving}
                isRecalculating={isAnySaving}
                stagedRewards={stagedRewards}
                onStagedRewardsChange={setStagedRewards}
              />
            }
          />
        )}
      </Modal>

      {/* Add Line Item Modal - Rendered outside main modal */}
      <AddLineItemModal
        opened={isAddLineItemModalOpen}
        onClose={handleAddLineItemModalClose}
        lineItemType={lineItemType || "service"}
        transaction={transaction}
        businessInfo={businessInfo}
        salonPackages={salonPackages}
        onLineItemAdded={handleLineItemAdded}
      />

      {/* Post-checkout fork for a gift card sold on this ticket: attach the
          minted card to a client, or text the recipient a claim link. */}
      {giftCardsEnabled && businessInfo && (
        <GiftCardPostCheckoutModal
          opened={showGiftCardPostCheckout}
          onClose={() => setShowGiftCardPostCheckout(false)}
          salonId={businessInfo.id}
          transactionUuid={transaction.transaction.id}
        />
      )}

      {/* Edit Service Modal */}
      {editingService && (
        <Modal
          opened={isEditServiceModalOpen}
          onClose={() => {
            setIsEditServiceModalOpen(false);
            setEditingService(null);
          }}
          title="Edit Service"
          size="lg"
          fullScreen={isMobile}
          centered
        >
          <TransactionBookedServiceModal
            mode="edit"
            service={editingService}
            transaction={transaction}
            onSubmit={handleLineItemUpdated}
            onCancel={() => {
              setIsEditServiceModalOpen(false);
              setEditingService(null);
            }}
            businessInfo={businessInfo}
          />
        </Modal>
      )}

      {/* Edit Product Modal */}
      {editingProduct && (
        <Modal
          opened={isEditProductModalOpen}
          onClose={() => {
            setIsEditProductModalOpen(false);
            setEditingProduct(null);
          }}
          title="Edit Product"
          size="lg"
          fullScreen={isMobile}
          centered
        >
          <ProductForm
            mode="edit"
            product={editingProduct}
            transaction={transaction}
            onSubmit={handleLineItemUpdated}
            onCancel={() => {
              setIsEditProductModalOpen(false);
              setEditingProduct(null);
            }}
          />
        </Modal>
      )}

      {/* Discount Modal */}
      <ApplyDiscountModal
        opened={isDiscountModalOpen}
        onClose={closeDiscountModal}
        lines={discountableLines}
        editingGroup={discountGroupToChange}
        initialPreset={quickDiscountPreset}
        onApply={handleApplyLineDiscounts}
      />
    </>
  );
}
